import { Calendar, CreditCard, Car, ShieldAlert, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { contactInfo } from "../data";

export default function Terms() {
  return (
    <div className="bg-white overflow-hidden">
      {/* Header Section */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 flex items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/treatment-detail.png"
            alt="Handelsbetingelser baggrund"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"></div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8"
        >
          <h1 className="text-4xl font-light tracking-tight text-white sm:text-6xl">Handelsbetingelser</h1>
          <p className="mt-6 text-lg text-stone-200 max-w-xl mx-auto">
            Gældende betingelser for tidsbestilling, afbud, betaling og kørsel hos Juhler Fodpleje.
          </p>
        </motion.div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          
          {/* Quick Summary Cards */}
          <div className="grid gap-6 sm:grid-cols-3 mb-16">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-stone-50 p-6 rounded-2xl border border-stone-100 text-center"
            >
              <Calendar className="h-8 w-8 text-orange-600 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-900 mb-1">Afbud</h3>
              <p className="text-xs text-stone-500">Senest 24 timer før aftalt tid af hensyn til planlægning.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-stone-50 p-6 rounded-2xl border border-stone-100 text-center"
            >
              <CreditCard className="h-8 w-8 text-orange-600 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-900 mb-1">Betaling</h3>
              <p className="text-xs text-stone-500">Kontant eller MobilePay umiddelbart efter endt behandling.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-stone-50 p-6 rounded-2xl border border-stone-100 text-center"
            >
              <Car className="h-8 w-8 text-orange-600 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-900 mb-1">Kørsel</h3>
              <p className="text-xs text-stone-500">Gældende kørselsgebyr på 75,- eller 100,- pr. hjemmebesøg.</p>
            </motion.div>
          </div>

          {/* Terms Text */}
          <div className="prose prose-stone max-w-none space-y-12 text-stone-600">
            <div>
              <h2 className="text-3xl font-light text-stone-900 mb-6">Generelle betingelser</h2>
              <p className="text-lg leading-relaxed">
                Når du booker en tid hos Juhler Fodpleje (uanset om det er til klinikbehandling eller som udekørende hjemmebesøg), accepterer du nedenstående handels- og forretningsbetingelser. Betingelserne er sat i verden for at sikre en professionel, punktlig og gensidigt respektfuld service.
              </p>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-600" />
                1. Tidsbestilling & Afbudspolitik
              </h3>
              <p className="leading-relaxed mb-4">
                Al tidsbestilling foregår via telefonisk kontakt (opkald eller SMS) på tlf. <strong>+45 41 83 65 30</strong>. Din booking er først endeligt bekræftet, når vi har indgået en klar aftale.
              </p>
              <p className="leading-relaxed mb-4">
                Skulle du blive forhindret i at møde op eller modtage mig i dit hjem, bedes du melde afbud hurtigst muligt:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Afbud skal ske senest 24 timer før</strong> den aftalte behandlingstid.</li>
                <li>Afbud skal ske telefonisk eller via SMS på tlf. +45 41 83 65 30.</li>
                <li>Ved for sent afbud (mindre end 24 timer før) eller ved forgæves kørsel, forbeholder jeg mig retten til at opkræve et gebyr svarende til 50% af behandlingens pris.</li>
                <li><strong>Ved udeblivelse uden afbud opkræves et gebyr på 250 kr.</strong></li>
              </ul>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-orange-600" />
                2. Priser & Betaling
              </h3>
              <p className="leading-relaxed mb-4">
                Alle oplyste priser på hjemmesiden er angivet i danske kroner (DKK) og er inklusiv moms.
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Betalingsmetoder:</strong> Jeg modtager betaling via <strong>MobilePay</strong> eller <strong>kontant</strong>.</li>
                <li><strong>Betalingstidspunkt:</strong> Betaling sker umiddelbart efter, at behandlingen er afsluttet. Ved institutioner og plejehjem kan der efter forudgående aftale afregnes via månedlig faktura eller bankoverførsel.</li>
              </ul>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Car className="h-5 w-5 text-orange-600" />
                3. Kørselsgebyr (ved hjemmebesøg)
              </h3>
              <p className="leading-relaxed mb-4">
                Ved udekørende behandlinger (hjemmebesøg) tillægges der et kørselsgebyr pr. besøg for at dække transportomkostninger og tid:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Nærområde (Kruså, Padborg, Faaborg, Millinge & Dyreborg):</strong> 75 kr. pr. behandling.</li>
                <li><strong>Øvrige dækkede områder (Gråsten, Broager, Egernsund, Tinglev m.fl.):</strong> 100 kr. pr. behandling.</li>
                <li>Ved flere behandlinger på samme adresse (f.eks. ægtepar, naboer eller på institutioner/plejehjem) aftales der særlige, reducerede kørselsvilkår.</li>
              </ul>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-orange-600" />
                4. Klientens ansvar (Helbred & Forberedelse)
              </h3>
              <p className="leading-relaxed mb-4">
                For at sikre en fuldstændig tryg og professionel behandling, påhviler der klienten et vist ansvar:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Oplysningspligt:</strong> Du er forpligtet til at oplyse om relevante helbredsforhold forud for behandlingen (f.eks. diabetes, blødersygdomme, brug af blodfortyndende medicin, smitsomme hudsygdomme eller allergier).</li>
                <li><strong>Forberedelse ved hjemmebesøg:</strong> Sørg gerne for, at der er ryddet en lille plads, hvor jeg kan stille mit udstyr op, samt adgang til en stikkontakt og en behagelig stol, du kan sidde i under behandlingen.</li>
              </ul>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4">
                5. Reklamation & Tilfredshedsgaranti
              </h3>
              <p className="leading-relaxed">
                Dine fødders velvære er mit vigtigste mål. Skulle du mod forventning opleve gener eller mangler efter en behandling, bedes du kontakte mig hurtigst muligt og senest 7 dage efter behandlingen. Såfremt der er tale om en berettiget reklamation, vil jeg naturligvis udbedre forholdet uden beregning.
              </p>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4">
                Kontakt & Virksomhedsoplysninger
              </h3>
              <div className="mt-6 bg-stone-50 rounded-2xl p-6 border border-stone-100 space-y-2 text-sm text-stone-700">
                <p className="font-semibold text-stone-900">{contactInfo.name}</p>
                <p>v/ {contactInfo.owner}</p>
                <p>{contactInfo.address}, {contactInfo.zipCity}</p>
                <p>Telefon: <a href={`tel:${contactInfo.phone}`} className="text-orange-900 hover:underline">{contactInfo.phoneDisplay}</a></p>
                <p>E-mail: <a href={`mailto:${contactInfo.email}`} className="text-orange-900 hover:underline">{contactInfo.email}</a></p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
